/**
 * Action: create_task
 *
 * Insert a row into `tasks`. The minimum viable shape — title +
 * optional due date, optional assignee, optional related person.
 * Solomon's planner can produce this from utterances like "remind
 * me to follow up with Sarah next Tuesday."
 *
 * Why no inverse?
 * --------------
 * Deleting a task isn't symmetric with creating one — the
 * just-created task may have been edited between create and undo,
 * and a hard delete would be a destructive surprise. We return no
 * inverse here so the UI doesn't offer one-click undo. A future
 * "soft-delete task" inverse can be added once tasks have a
 * deleted_at column we can rely on.
 */

import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { logAudit } from "@/lib/audit";
import { registerAction } from "../registry";
import type { ActionDefinition, ActionResult } from "../types";

const createTaskParams = z.object({
  title: z.string().trim().min(1).max(500),
  /** ISO date string (YYYY-MM-DD) or full ISO timestamp. */
  due_date: z.string().min(1).optional().nullable(),
  /** auth.users.id of the assignee. */
  assignee_id: z.string().guid().optional().nullable(),
  /** people.id this task relates to. Shown as the task's subject. */
  person_id: z.string().guid().optional().nullable(),
  priority: z.enum(["low", "medium", "high", "urgent"]).default("medium"),
  description: z.string().max(5000).optional().nullable(),
});

type CreateTaskParams = z.infer<typeof createTaskParams>;

async function resolvePersonName(
  organizationId: string,
  personId: string | null | undefined,
): Promise<string | null> {
  if (!personId) return null;
  const { data, error } = await supabase
    .from("people")
    .select("first_name, last_name")
    .eq("organization_id", organizationId)
    .eq("id", personId)
    .maybeSingle();
  if (error || !data) return null;
  const name = [data.first_name ?? "", data.last_name ?? ""]
    .map((s) => s.trim())
    .filter(Boolean)
    .join(" ");
  return name || null;
}

const createTaskDefinition: ActionDefinition<CreateTaskParams> = {
  id: "create_task",
  label: "Create task",
  description: "Create a new task, optionally linked to a person.",
  requiredTier: "member",
  paramsSchema: createTaskParams,
  // Tasks have meaningful side effects (show up on dashboards,
  // notify assignees via cron-reminders) so we default to confirm.
  defaultRequiresConfirm: true,
  async buildPreview(params, ctx) {
    const personName = await resolvePersonName(
      ctx.organizationId,
      params.person_id,
    );
    const parts: string[] = [`Create task "${params.title}"`];
    if (personName) parts.push(`for ${personName}`);
    if (params.due_date) parts.push(`due ${params.due_date}`);
    return { summary: parts.join(" ") };
  },
  async execute(params, ctx): Promise<ActionResult> {
    const personName = await resolvePersonName(
      ctx.organizationId,
      params.person_id,
    );

    const { data, error } = await supabase
      .from("tasks")
      .insert({
        organization_id: ctx.organizationId,
        title: params.title,
        description: params.description ?? null,
        // Tasks table uses string status with the canonical
        // 'todo' default elsewhere in the codebase (see
        // ProjectDetail.tsx).
        status: "todo",
        priority: params.priority,
        due_date: params.due_date ?? null,
        assignee_id: params.assignee_id ?? null,
        person_id: params.person_id ?? null,
      })
      .select("id")
      .single();

    if (error || !data) {
      return {
        ok: false,
        summary: "Failed to create task",
        error: error?.message ?? "Unknown insert failure",
      };
    }

    const audit = await logAudit({
      action: "task.create",
      tableName: "tasks",
      recordId: data.id,
      organizationId: ctx.organizationId,
      userId: ctx.userId,
      newData: {
        title: params.title,
        person_id: params.person_id ?? null,
        person_name: personName,
        due_date: params.due_date ?? null,
        assignee_id: params.assignee_id ?? null,
        priority: params.priority,
        source: ctx.source,
        reason: ctx.reason,
      },
    });

    const summary = personName
      ? `Created task "${params.title}" for ${personName}`
      : `Created task "${params.title}"`;

    return {
      ok: true,
      summary,
      auditLogId: audit?.id ?? null,
      // No inverse — see file header.
      inverse: null,
    };
  },
};

registerAction(createTaskDefinition);

export { createTaskDefinition };
